import {useCallback, useEffect, useRef, useState} from "react";
import {CardModal} from "../Modals/Card/CardModal";
import {CardLabels} from "./CardLabels";
import {useDispatch, useSelector} from "react-redux";
import {SET_ACTIVE_CARD, SET_BOARD_LISTS} from "../../../../../store/constants";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {SortableCard} from "../../../../../components/Sortable/SortableCard";
import "./cards.css";
import {API_URL, IMAGE_URL, SOCKET} from "../../../../../URL";

export const Cards = ({list, overCardId}) => {
    const dispatch = useDispatch();
    const activeCard = useSelector((state) => state.boards.activeCard);
    const activeBoard = useSelector(state => state.boards.activeBoard);
    const boardLists = useSelector((state) => state.boards.boardLists || []);
    const [editingCardId, setEditingCardId] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const editRef = useRef(null);
    const isEmptyActiveCard = !activeCard || Object.keys(activeCard).length === 0;
    const cards = list?.cards || [];
    const user = useSelector(state => state.user.user);
    const userRole = activeBoard?.rbac?.find(
        (el) => el.user_id === user?.id
    )?.role;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (editRef.current && !editRef.current.contains(event.target)) {
                setEditingCardId(null);
                setEditTitle("");
            }
        };

        const handleEsc = (event) => {
            if (event.key === "Escape") {
                setEditingCardId(null);
                setEditTitle("");
            }
        };

        // Ավելացնել touch event-ները mobile-ի համար
        const handleTouchOutside = (event) => {
            if (editRef.current && !editRef.current.contains(event.target)) {
                setEditingCardId(null);
                setEditTitle("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEsc);
        document.addEventListener("touchstart", handleTouchOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEsc);
            document.removeEventListener("touchstart", handleTouchOutside);
        };
    }, []);

    const handleOpenCard = (card) => {
        dispatch({
            type: SET_ACTIVE_CARD,
            payload: card,
        });
    };

    const handleStartEdit = (e, card) => {
        e.stopPropagation();
        setEditingCardId(card.id);
        setEditTitle(card.name || "");
    };

    const updateCardNameInStore = useCallback((cardId, name) => {
        dispatch({
            type: SET_BOARD_LISTS,
            payload: boardLists.map((boardList) => ({
                ...boardList,
                cards: (boardList.cards || []).map((boardCard) =>
                    boardCard.id === cardId
                        ? {...boardCard, name}
                        : boardCard
                ),
            })),
        });

        if (activeCard?.id === cardId) {
            dispatch({
                type: SET_ACTIVE_CARD,
                payload: {...activeCard, name},
            });
        }
    }, [activeCard, boardLists, dispatch]);

    const updateCardArchiveInStore = useCallback((cardId, isArchive) => {
        dispatch({
            type: SET_BOARD_LISTS,
            payload: boardLists.map((boardList) => ({
                ...boardList,
                cards: (boardList.cards || []).map((boardCard) =>
                    boardCard.id === cardId
                        ? {...boardCard, is_archive: isArchive}
                        : boardCard
                ),
            })),
        });
    }, [boardLists, dispatch]);

    const deleteCardFromStore = useCallback((cardId) => {
        dispatch({
            type: SET_BOARD_LISTS,
            payload: boardLists.map((boardList) => ({
                ...boardList,
                cards: (boardList.cards || []).filter(
                    (boardCard) => boardCard.id !== cardId
                ),
            })),
        });
    }, [boardLists, dispatch]);

    const handleSaveEdit = async (card) => {
        const newTitle = editTitle.trim();

        if (!newTitle) return;

        const response = await fetch(API_URL + "/post/rename_board_card/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                id: card.id,
                name: newTitle
            })
        });
        if (!response.ok) return;

        const data = await response.json();

        updateCardNameInStore(data.id, data.name);
        setEditingCardId(null);
        setEditTitle("");

        SOCKET.emit("rename_board_card", {
            board_id: list?.board_id,
            card: data,
        });
    };

    useEffect(() => {
        const handleRenameBoardCard = (data) => {
            if (Number(data?.board_id) !== Number(list?.board_id)) return;
            if (!data?.card) return;

            updateCardNameInStore(data.card.id, data.card.name);
        };

        SOCKET.on("rename_board_card", handleRenameBoardCard);

        return () => {
            SOCKET.off("rename_board_card", handleRenameBoardCard);
        };
    }, [list?.board_id, updateCardNameInStore]);

    const handleArchiveCard = async (id) => {
        const response = await fetch(API_URL + "/post/handle_board_card/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({id})
        });

        if (!response.ok) return;

        const data = await response.json();
        updateCardArchiveInStore(data.id, data.is_archive);
        setEditingCardId(null);

        SOCKET.emit("handle_board_card", {
            board_id: list?.board_id,
            card: data,
        });
    };

    const handleDeleteCard = async (id) => {
        const response = await fetch(API_URL + "/delete/remove_board_card/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({id})
        });

        if (!response.ok) return;

        deleteCardFromStore(id);
        setEditingCardId(null);

        SOCKET.emit("remove_board_card", {
            board_id: list?.board_id,
            card_id: id,
        });
    };

    useEffect(() => {
        const handleRemoveBoardCard = (data) => {
            if (Number(data?.board_id) !== Number(list?.board_id)) return;
            if (!data?.card_id) return;

            deleteCardFromStore(data.card_id);
        };

        SOCKET.on("remove_board_card", handleRemoveBoardCard);

        return () => {
            SOCKET.off("remove_board_card", handleRemoveBoardCard);
        };
    }, [list?.board_id, deleteCardFromStore]);

    useEffect(() => {
        const handleArchiveBoardCard = (data) => {
            if (Number(data?.board_id) !== Number(list?.board_id)) return;
            if (!data?.card) return;

            updateCardArchiveInStore(data.card.id, data.card.is_archive);
        };

        SOCKET.on("handle_board_card", handleArchiveBoardCard);

        return () => {
            SOCKET.off("handle_board_card", handleArchiveBoardCard);
        };
    }, [list?.board_id, updateCardArchiveInStore]);

    return (
        <>
            <SortableContext
                items={cards.filter((card) => !card.is_archive).map((card) => `card-${card.id}`)}
                strategy={verticalListSortingStrategy}
            >
                <div className="boardList__cards">
                    {cards.filter((el) => !el.is_archive).map((card) => {
                        const isEditing = editingCardId === card.id;
                        const coverAttachment = card?.attachments?.find((el) => el.is_cover);
                        const attachmentCount = card?.attachments?.length || 0;

                        return (
                            <SortableCard
                                key={card.id}
                                card={card}
                                isOver={overCardId === card.id}
                                onClick={() => !isEditing && handleOpenCard(card)}
                                isEditing={isEditing}
                                disabled={userRole === 1}
                            >
                                {!isEditing ? (
                                    <div className="boardCard__view">
                                        <button
                                            type="button"
                                            className="boardCard__editBtn"
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => {
                                                // Mobile-ի համար ավելի հեշտ սեղմում
                                                e.stopPropagation();
                                            }}
                                            onClick={(e) => userRole !== 1 && handleStartEdit(e, card)}
                                        >
                                            ✎
                                        </button>

                                        {coverAttachment && (
                                            <img
                                                className="boardCard__cover"
                                                src={IMAGE_URL + coverAttachment.url}
                                                alt={coverAttachment.title || card.name}
                                                loading="lazy"
                                                draggable={false}
                                            />
                                        )}

                                        <div className="boardCard__content">
                                            <CardLabels card={card}/>

                                            <div className="boardCard__title">
                                                {card.name}
                                            </div>

                                            {attachmentCount > 0 && (
                                                <div className="boardCard__badges">
                                                    <span className="boardCard__badge">
                                                        📎 {attachmentCount}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className="boardCardQuickEditWrap"
                                        ref={editRef}
                                        onClick={(e) => e.stopPropagation()}
                                        onTouchStart={(e) => e.stopPropagation()}
                                    >
                                        <div className="boardCardQuickEdit">
                                            <textarea
                                                className="boardCardQuickEdit__input"
                                                value={editTitle}
                                                onChange={(e) => setEditTitle(e.target.value)}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSaveEdit(card);
                                                    }
                                                }}
                                                // Mobile-ի համար
                                                onTouchStart={(e) => e.stopPropagation()}
                                            />

                                            <div className="boardCardQuickEdit__actions">
                                                <button
                                                    type="button"
                                                    className="boardCardQuickEdit__save"
                                                    onClick={() => handleSaveEdit(card)}
                                                    onTouchStart={(e) => e.stopPropagation()}
                                                >
                                                    Save
                                                </button>

                                                <button
                                                    type="button"
                                                    className="boardCardQuickEdit__close"
                                                    onClick={() => {
                                                        setEditingCardId(null);
                                                        setEditTitle("");
                                                    }}
                                                    onTouchStart={(e) => e.stopPropagation()}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>

                                        <div className="cardQuickActions">
                                            <button
                                                type="button"
                                                className="cardQuickActions__item cardQuickActions__item--archive"
                                                onClick={() => handleArchiveCard(card.id)}
                                                onTouchStart={(e) => e.stopPropagation()}
                                            >
                                                <span className="cardQuickActions__icon">🗄</span>
                                                <span>Archive</span>
                                            </button>

                                            <button
                                                type="button"
                                                className="cardQuickActions__item cardQuickActions__item--danger"
                                                onClick={() => handleDeleteCard(card.id)}
                                                onTouchStart={(e) => e.stopPropagation()}
                                            >
                                                <span className="cardQuickActions__icon">🗑</span>
                                                <span>Delete</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </SortableCard>
                        );
                    })}
                </div>
            </SortableContext>

            {!isEmptyActiveCard && (
                <CardModal
                    card={activeCard}
                    onClose={() =>
                        dispatch({
                            type: SET_ACTIVE_CARD,
                            payload: {},
                        })
                    }
                />
            )}
        </>
    );
};
